import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const revenueModels = [
    'subscription',
    'transactional'
]

const CostValueDriverPaymentForm = forwardRef(({
    currency,
    outcome,
    outcomeList,
    editPaymentDriverId,
    editPaymentDriver,
    formActions,
    onSubmit
}, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [positiveDriver, setPositiveDriver] = useState(true);

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    const handleRevenueModelChange = (event) => {
        formik.setFieldValue('revenue_model', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };


    const tooltipText = {
        annualTurnover: "The total revenue generated by a company over a 12-month period from its normal business activities.",
        percentCreditSales: "The percentage of total sales made on credit rather than paid upfront, indicating how much revenue is tied up in receivables.",
        dsoChange: "DSO (Days Sales Outstanding) measures the average number of days it takes to collect payment after a sale; a reduction improves cash flow, while an increase delays it.",
        costOfBorrowing: "The effective interest rate a company pays on its borrowed funds, including loans, credit lines, or bonds."
    };

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editPaymentDriverId && editPaymentDriver) {
            setPositiveDriver(editPaymentDriver.positive_driver);
            // formik.setValues({ ...editPaymentDriver });

            formik.setFieldValue("share_credit_sales", editPaymentDriver["share_credit_sales"])
            formik.setFieldValue("dso_decrease", editPaymentDriver["dso_decrease"])
            formik.setFieldValue("cost_of_borrowing", editPaymentDriver["cost_of_borrowing"])
            formik.setFieldValue("revenue_model", editPaymentDriver["revenue_model"])
            formik.setFieldValue("annual_contract_value", formatNumberThsndAndDec(editPaymentDriver["annual_contract_value"]))
            formik.setFieldValue("transaction_value", formatNumberThsndAndDec(editPaymentDriver["transaction_value"]))
            formik.setFieldValue("transaction_frequency", formatNumberThsndAndDec(editPaymentDriver["transaction_frequency"]))
            formik.setFieldValue("positive_driver", editPaymentDriver["positive_driver"])
            formik.setFieldValue("outcome", editPaymentDriver["outcome"])
            formik.setFieldValue("driver_name", editPaymentDriver["driver_name"])
            formik.setFieldValue("comment", editPaymentDriver["comment"])            
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editPaymentDriverId]);

    useEffect(() => {
        formik.setFieldValue('driver_name', `${positiveDriver ? 'Faster' : 'Slower'} payment`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver]);

    const initialValues = {
        id: null,
        cost_type: 'payment',
        outcome: '',
        driver_name: `${positiveDriver ? 'Faster' : 'Slower'} payment`,
        comment: '',
        positive_driver: true,
        type: 'payment',
        currency: currency,
        share_credit_sales: 100,
        dso_decrease: 0,
        cost_of_borrowing: 6,
        annual_contract_value: 0,
        transaction_value: 0,
        transaction_frequency: 0,
        revenue_model: revenueModels[0]
    };

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = values => {
        const errors = {};

        if (!isNumeric(values.share_credit_sales)) {
            errors.share_credit_sales = 'Must be numeric value';
        } else if (!isPositive(values.share_credit_sales)) {
            errors.share_credit_sales = 'Must be positive or zero';
        } else if (values.share_credit_sales > 100) {
            errors.share_credit_sales = 'Not more than 100';
        }

        if (!isNumeric(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be numeric value';
        } else if (!isPositive(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.transaction_value)) {
            errors.transaction_value = 'Must be numeric value';
        } else if (!isPositive(values.transaction_value)) {
            errors.transaction_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be numeric value';
        } else if (!isPositive(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be positive or zero';
        }

        if (!isNumeric(values.dso_decrease) || !isPositive(values.dso_decrease)) {
            errors.dso_decrease = 'Must be a non-negative number';
        }
        if (!isNumeric(values.cost_of_borrowing) || !isPositive(values.cost_of_borrowing)) {
            errors.cost_of_borrowing = 'Must be a non-negative number';
        }
        return errors;
    };

    const formik = useFormik({
        initialValues,
        validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.resetForm();
            setPositiveDriver(true);
        }
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 800 }}>
            <Grid container columnSpacing={2} rowSpacing={2} alignItems="center" sx={{ mt: 1 }}>

                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            1. Revenue per customer
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption">
                            Your Customers' revenue model{tooltip(generalTooltipText.revenueModel)}
                        </Typography>
                        <Box>
                            <Select
                                open={open}
                                onClose={handleClose}
                                onOpen={handleOpen}
                                value={formik.values.revenue_model}
                                onChange={handleRevenueModelChange}
                                size='small'
                            >
                                {revenueModels.map((model) => (
                                    <MenuItem key={model} value={model}>
                                        {firstCharToUpper(model)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {formik.values.revenue_model === 'subscription' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={3}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Annual Contract Value (ACV){tooltip(generalTooltipText.acv)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={9} />
                                <Grid item xs={3}>
                                    <TextFieldCustom
                                        name="annual_contract_value"
                                        startAdornment={currency}
                                        value={formik.values.annual_contract_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("annual_contract_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "annual_contract_value") && Boolean(getIn(formik.errors, "annual_contract_value"))}
                                        helperText={getIn(formik.touched, "annual_contract_value") && getIn(formik.errors, "annual_contract_value")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}
                {formik.values.revenue_model === 'transactional' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={3}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction value{tooltip(generalTooltipText.transactionValue)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction frequency{tooltip(generalTooltipText.transactionFrequency)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3} />
                                <Grid item xs={3}>
                                    <TextFieldCustom
                                        name="transaction_value"
                                        startAdornment={currency}
                                        value={formik.values.transaction_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_value") && Boolean(getIn(formik.errors, "transaction_value"))}
                                        helperText={getIn(formik.touched, "transaction_value") && getIn(formik.errors, "transaction_value")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextFieldCustom
                                        name="transaction_frequency"
                                        endAdornment={
                                            <Typography variant="caption">
                                                times/year
                                            </Typography>
                                        }
                                        value={formik.values.transaction_frequency}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_frequency", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_frequency") && Boolean(getIn(formik.errors, "transaction_frequency"))}
                                        helperText={getIn(formik.touched, "transaction_frequency") && getIn(formik.errors, "transaction_frequency")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}

                {/* 2. Payment */}
                <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        2. Payment
                    </Typography>
                </Grid>

                {/* <Grid item xs={3}>
                    <Typography variant="caption">
                        Annual turnover{tooltip(tooltipText.annualTurnover)}
                    </Typography>
                    <TextFieldCustom
                        name="annual_turnover"
                        startAdornment={currency}
                        value={formik.values.annual_turnover}
                        onChange={e => {
                            const v = formatNumberThsndAndDec(e.target.value);
                            formik.setFieldValue('annual_turnover', v || '');
                        }}
                        onBlur={formik.handleBlur}
                        error={!!getIn(formik.errors, 'annual_turnover')}
                        helperText={getIn(formik.touched, 'annual_turnover') && getIn(formik.errors, 'annual_turnover')}
                        fullWidth
                    />
                </Grid> */}

                <Grid item xs={3}>
                    <Typography variant="caption">
                        % credit sales{tooltip(tooltipText.percentCreditSales)}
                    </Typography>
                    <TextFieldCustom
                        name="share_credit_sales"
                        endAdornment="%"
                        value={formik.values.share_credit_sales}
                        onChange={e => {
                            const v = formatNumberThsndAndDec(e.target.value);
                            formik.setFieldValue('share_credit_sales', v || '');
                        }}
                        onBlur={formik.handleBlur}
                        error={!!getIn(formik.errors, 'share_credit_sales')}
                        helperText={getIn(formik.touched, 'share_credit_sales') && getIn(formik.errors, 'share_credit_sales')}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={9} />

                <Grid item xs={3}>
                    <Typography variant="caption">
                        DSO {positiveDriver ? 'reduction' : 'increase'}{tooltip(tooltipText.dsoChange)}
                    </Typography>
                    <TextFieldCustom
                        name="dso_decrease"
                        endAdornment={
                            <Typography variant='caption'>
                                days
                            </Typography>
                        }
                        value={formik.values.dso_decrease}
                        onChange={e => formik.setFieldValue('dso_decrease', e.target.value)}
                        onBlur={formik.handleBlur}
                        error={!!getIn(formik.errors, 'dso_decrease')}
                        helperText={getIn(formik.touched, 'dso_decrease') && getIn(formik.errors, 'dso_decrease')}
                        greenBackground={positiveDriver}
                        redBackground={!positiveDriver}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={3}>
                    <Typography variant="caption">
                        Cost of borrowing{tooltip(tooltipText.costOfBorrowing)}
                    </Typography>
                    <TextFieldCustom
                        name="cost_of_borrowing"
                        endAdornment='%'
                        value={formik.values.cost_of_borrowing}
                        onChange={e => formik.setFieldValue('cost_of_borrowing', e.target.value)}
                        onBlur={formik.handleBlur}
                        error={!!getIn(formik.errors, 'cost_of_borrowing')}
                        helperText={getIn(formik.touched, 'cost_of_borrowing') && getIn(formik.errors, 'cost_of_borrowing')}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={6} />

                {/* 3. Driver details */}
                <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        3. Driver details
                    </Typography>
                </Grid>

                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
                <Grid item xs={10} />

                {!outcome &&
                    (<React.Fragment>
                        <Grid item xs={2}>
                            <Stack direction="column" gap={1}>
                                <Typography variant="caption">
                                    Outcome
                                </Typography>
                                <Box>
                                    <Select
                                        open={openOutcome}
                                        onClose={handleOutcomeClose}
                                        onOpen={handleOutcomeOpen}
                                        value={formik.values.outcome}
                                        onChange={handleOutcomeChange}
                                        size='small'
                                    >
                                        {outcomeList.map((outcome) => (
                                            <MenuItem key={outcome} value={outcome}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {outcome}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={10} />
                    </React.Fragment>)
                }
                <Grid item xs={12}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Name of driver
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Comment
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="driver_name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextFieldCustom
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    );
});

export default CostValueDriverPaymentForm;